<template>
  <span class="inspection-status-indicator-badge" :class="badgeClass">
    {{ label }}
  </span>
</template>

<script setup>
import { computed } from "vue";
import {
  CHECKPOINT_STATUSES,
  CHECKPOINT_STATUS_LABELS,
} from "./inspectionStatusIndicatorBadgeConstants";

/**
 * InspectionStatusIndicatorBadge
 *
 * A read-only pill that reports a single checkpoint's overall status to the
 * employee. It is the first badge in the MetaRow of AsEmployeeCheckpointInspectionCard.
 *
 * The component is a thin wrapper around the shared 2.0 Basic Badges base styling
 * (the same base used by CheckpointQuizBadge / CheckpointInspectionBadge): only the
 * label text and the colour trio change per status — there is no separate label or
 * colour prop, no events and no slots. Nothing is derived inside the badge; the
 * parent passes the resolved `checkpointStatus`.
 *
 * Do not confuse with `InspectionStatusBadge` (AsEmployeeInspectionStatusBadge) —
 * an older, unrelated component of a similar name. Keep the names distinct in code.
 *
 * Inspector-only checkpoints never render this badge (confirmed 2026-09-01): the
 * parent card opens those MetaRows with InspectorOnlyBadge instead, so the card
 * status -> checkpointStatus mapping deliberately has no entry for them.
 *
 * Integration of this badge is gated by the LaunchDarkly flag `idea-MDP-452`
 * (New Checkpoint Edit and Inspection) at the consumer call site.
 */
const props = defineProps({
  /**
   * The checkpoint's overall status. Maps 1:1 to the Figma Checkpoint Status
   * variant axis and drives both the label and the colour together.
   */
  checkpointStatus: {
    type: String,
    required: true,
    validator: (value) => CHECKPOINT_STATUSES.includes(value),
  },
});

const normalizedStatus = computed(() => (props.checkpointStatus || "").trim());

const label = computed(() => CHECKPOINT_STATUS_LABELS[normalizedStatus.value] || "");

const badgeClass = computed(() => {
  const status = normalizedStatus.value.toLowerCase().replace(/\s+/g, "-");
  return status ? `inspection-status-indicator-badge--${status}` : "";
});
</script>

<style scoped>
.inspection-status-indicator-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: var(--spacing-xxs, 2px) var(--spacing-sm, 6px);
  border: 1px solid;
  border-radius: var(--radius-sm, 6px);
  box-sizing: border-box;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0;
  text-align: center;
  white-space: nowrap;
  pointer-events: none;
  cursor: default;
  user-select: none;
}

/* Not Started — gray */
.inspection-status-indicator-badge--not-started {
  color: var(--component-colors-utility-gray-utility-gray-700, #344054);
  border-color: var(--component-colors-utility-gray-utility-gray-200, #eaecf0);
  background: var(--component-colors-utility-gray-utility-gray-50, #f9fafb);
}

/* In Progress — warning (orange). Note the lowercase "in": "Checkpoint in Progress". */
.inspection-status-indicator-badge--in-progress {
  color: var(--component-colors-utility-warning-utility-warning-700, #b54708);
  border-color: var(--component-colors-utility-warning-utility-warning-200, #fedf89);
  background: var(--component-colors-utility-warning-utility-warning-50, #fffaeb);
}

/* Passed — success (green) */
.inspection-status-indicator-badge--passed {
  color: var(--component-colors-utility-success-utility-success-700, #067647);
  border-color: var(--component-colors-utility-success-utility-success-200, #abefc6);
  background: var(--component-colors-utility-success-utility-success-50, #ecfdf3);
}

/* Failed — error (red) */
.inspection-status-indicator-badge--failed {
  color: var(--component-colors-utility-error-utility-error-700, #b42318);
  border-color: var(--component-colors-utility-error-utility-error-200, #fecdca);
  background: var(--component-colors-utility-error-utility-error-50, #fef3f2);
}

/* Reset — primary (blue) */
.inspection-status-indicator-badge--reset {
  color: var(--component-colors-utility-primary-utility-primary-700, #175cd3);
  border-color: var(--component-colors-utility-primary-utility-primary-200, #b2ddff);
  background: var(--component-colors-utility-primary-utility-primary-50, #eff8ff);
}

/* Not Applicable — indigo. Deliberately close to Reset's blue (accepted as designed, Eric Kish 2026-09-01). */
.inspection-status-indicator-badge--not-applicable {
  color: var(--component-colors-utility-indigo-utility-indigo-700, #3538cd);
  border-color: var(--component-colors-utility-indigo-utility-indigo-200, #c7d7fe);
  background: var(--component-colors-utility-indigo-utility-indigo-50, #eef4ff);
}
</style>
